import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Chip,
  Alert,
  Divider,
  CircularProgress,
} from "@mui/material";
import FormattableInput from "../common/formattable-input";
import { PhraseStatus } from "../../api/phrasebook";
import {
  LanguageCode,
  languageCodeToLabel,
} from "../../pages/playground/types";
import { EditorPhrase, checkDuplicatePhrase } from "../../api/phrasebook_editor";

interface PhraseEditDialogProps {
  open: boolean;
  phrase: EditorPhrase | null;
  brands: Array<{ id: string; name: string }>;
  onClose: () => void;
  onSave: (updatedPhrase: EditorPhrase) => Promise<void>;
  onSearchForPhrase?: (phrase: string) => void;
}

const PhraseEditDialog: React.FC<PhraseEditDialogProps> = ({
  open,
  phrase,
  brands,
  onClose,
  onSave,
  onSearchForPhrase,
}) => {
  const [editData, setEditData] = useState<Partial<EditorPhrase>>({});
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicatePhrase, setDuplicatePhrase] = useState<EditorPhrase | null>(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  
  const isNewPhrase = !phrase?.id;

  // Debounced duplicate check function
  const checkForDuplicate = useCallback(async (phraseText: string, originalLang: string) => {
    if (!phraseText.trim() || !originalLang.trim() || !isNewPhrase) {
      setDuplicatePhrase(null);
      return;
    }
    
    setCheckingDuplicate(true);
    try {
      const response = await checkDuplicatePhrase(phraseText, originalLang);
      if (response.data.data.exists && response.data.data.phrase) {
        setDuplicatePhrase(response.data.data.phrase);
      } else {
        setDuplicatePhrase(null);
      }
    } catch (err) {
      console.error("Error checking for duplicate:", err);
      setDuplicatePhrase(null);
    } finally {
      setCheckingDuplicate(false);
    }
  }, [isNewPhrase]);

  useEffect(() => {
    if (phrase) {
      // Ensure translation_status is always fully populated with all language codes
      const fullTranslationStatus = Object.values(LanguageCode).reduce(
        (acc, lang) => ({ ...acc, [lang]: phrase.translation_status?.[lang] || false }),
        {} as Record<LanguageCode, boolean>
      );
      
      setEditData({
        phrase: phrase.phrase,
        status: phrase.status,
        suitable_for_title: phrase.suitable_for_title,
        suitable_for_description: phrase.suitable_for_description,
        suitable_for_bullet_points: phrase.suitable_for_bullet_points,
        original_language: phrase.original_language,
        translation_status: fullTranslationStatus,
      });
      setSelectedBrandIds(phrase.brand_ids || []);
      
      // Reset duplicate state when phrase changes
      setDuplicatePhrase(null);
    }
  }, [phrase]);
  
  // Debounced duplicate checking when phrase text or original language changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (editData.phrase && editData.original_language) {
        checkForDuplicate(editData.phrase, editData.original_language);
      }
    }, 1000); // 1 second delay
    
    return () => clearTimeout(timeoutId);
  }, [editData.phrase, editData.original_language, checkForDuplicate]);

  const handleBrandToggle = (brandId: string) => {
    if (selectedBrandIds.includes(brandId)) {
      setSelectedBrandIds(selectedBrandIds.filter((id) => id !== brandId));
    } else {
      setSelectedBrandIds([...selectedBrandIds, brandId]);
    }
  };

  const handleSave = async () => {
    if (!phrase || !editData.phrase?.trim()) return;

    setSaving(true);
    setError(null);

    try {
      const phraseToSave: EditorPhrase = {
        ...phrase,
        ...editData,
        brand_ids: selectedBrandIds,
      };

      await onSave(phraseToSave);
      onClose();
    } catch (err: unknown) {
      const errorMessage =
        (err as { message?: string })?.message || "Failed to save phrase";
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!phrase) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isNewPhrase ? "Add New Phrase" : "Edit Phrase"}
        {!isNewPhrase && phrase.usage_count && phrase.usage_count > 0 && (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ display: "block", mt: 1 }}
          >
            ⚠️ This phrase is used in {phrase.usage_count} jobs. Changes will
            affect all existing uses.
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {/* Phrase Text */}
          <Box>
            <Box sx={{ position: "relative" }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                Phrase *
              </Typography>
              <Box sx={{
                border: "1px solid rgba(0, 0, 0, 0.23)",
                borderRadius: "4px",
                padding: "8.5px 14px",
                minHeight: "40px",
                display: "flex",
                alignItems: "center",
                "&:hover": { borderColor: "rgba(0, 0, 0, 0.87)" },
                "&:focus-within": { borderColor: "primary.main", borderWidth: "2px", padding: "7.5px 13px" },
              }}>
                <FormattableInput
                  value={editData.phrase || ""}
                  onChange={(val) =>
                    setEditData({ ...editData, phrase: val })
                  }
                  placeholder="Enter phrase"
                />
                {checkingDuplicate && (
                  <CircularProgress size={20} sx={{ ml: 1, flexShrink: 0 }} />
                )}
              </Box>
            </Box>
            
            {/* Duplicate phrase warning */}
            {duplicatePhrase && (
              <Alert 
                severity="warning" 
                sx={{ mt: 1 }}
                action={
                  onSearchForPhrase ? (
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => {
                        onSearchForPhrase(`"${duplicatePhrase.phrase}"`);
                        onClose();
                      }}
                    >
                      Search for Phrase
                    </Button>
                  ) : undefined
                }
              >
                <Typography variant="body2">
                  This phrase already exists with the same original language.
                  {duplicatePhrase.usage_count && duplicatePhrase.usage_count > 0 && (
                    <> It's currently used in {duplicatePhrase.usage_count} jobs.</>
                  )}
                </Typography>
              </Alert>
            )}
          </Box>

          {/* Original Language */}
          <FormControl fullWidth required>
            <InputLabel>Original Language *</InputLabel>
            <Select
              value={editData.original_language || ""}
              onChange={(e) => {
                const newLang = e.target.value;
                // Create a fully populated translation status with all language codes
                const fullTranslationStatus = Object.values(LanguageCode).reduce(
                  (acc, lang) => ({ ...acc, [lang]: editData.translation_status?.[lang] || false }),
                  {} as Record<LanguageCode, boolean>
                );
                // Set the original language to true
                fullTranslationStatus[newLang as LanguageCode] = true;
                
                setEditData({ 
                  ...editData, 
                  original_language: newLang,
                  translation_status: fullTranslationStatus
                });
              }}
              label="Original Language *"
              required
            >
              {Object.values(LanguageCode).map((langCode) => (
                <MenuItem key={langCode} value={langCode}>
                  {languageCodeToLabel(langCode)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status */}
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={editData.status || ""}
              onChange={(e) =>
                setEditData({
                  ...editData,
                  status: e.target.value as PhraseStatus,
                })
              }
              label="Status"
            >
              <MenuItem value={PhraseStatus.UNREVIEWED}>Unreviewed</MenuItem>
              <MenuItem value={PhraseStatus.INTERNALLY_REVIEWED}>
                Internally Reviewed
              </MenuItem>
              <MenuItem value={PhraseStatus.EXTERNALLY_REVIEWED}>
                Externally Reviewed
              </MenuItem>
            </Select>
          </FormControl>

          {/* Suitability */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Suitability
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editData.suitable_for_title || false}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        suitable_for_title: e.target.checked,
                      })
                    }
                  />
                }
                label="Suitable for Title"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editData.suitable_for_description || false}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        suitable_for_description: e.target.checked,
                      })
                    }
                  />
                }
                label="Suitable for Description"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editData.suitable_for_bullet_points || false}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        suitable_for_bullet_points: e.target.checked,
                      })
                    }
                  />
                }
                label="Suitable for Bullet Points"
              />
            </Box>
          </Box>

          <Divider />

          {/* Brand Associations */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Brand Associations
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {brands.map((brand) => {
                const isSelected = selectedBrandIds.includes(brand.id);
                const hasUsage =
                  !isNewPhrase &&
                  phrase.brand_ids?.includes(brand.id) &&
                  phrase.usage_count &&
                  phrase.usage_count > 0;

                return (
                  <Chip
                    key={brand.id}
                    label={brand.name}
                    size="small"
                    clickable={!hasUsage}
                    variant={isSelected ? "filled" : "outlined"}
                    color={
                      hasUsage ? "warning" : isSelected ? "primary" : "default"
                    }
                    onClick={() => !hasUsage && handleBrandToggle(brand.id)}
                    title={
                      hasUsage
                        ? "Cannot remove - phrase has usage in this brand"
                        : "Click to toggle"
                    }
                  />
                );
              })}
            </Box>
            {!isNewPhrase &&
              phrase.usage_count !== undefined &&
              phrase.usage_count > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Brands with usage cannot be removed while the phrase is being
                  used.
                </Typography>
              )}
          </Box>

          <Divider />

          {/* Translation Status */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Translation Status
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {Object.entries(editData.translation_status || {}).map(
                ([langCode, isAvailable]) => (
                  <FormControlLabel
                    key={langCode}
                    control={
                      <Checkbox
                        size="small"
                        checked={isAvailable}
                        onChange={(e) => {
                          // Ensure we always have a fully populated translation status
                          const fullTranslationStatus = Object.values(LanguageCode).reduce(
                            (acc, lang) => ({ ...acc, [lang]: editData.translation_status?.[lang] || false }),
                            {} as Record<LanguageCode, boolean>
                          );
                          fullTranslationStatus[langCode as LanguageCode] = e.target.checked;
                          
                          setEditData({
                            ...editData,
                            translation_status: fullTranslationStatus,
                          });
                        }}
                      />
                    }
                    label={languageCodeToLabel(langCode as LanguageCode)}
                  />
                )
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !editData.phrase?.trim() || (isNewPhrase && !editData.original_language) || (isNewPhrase && duplicatePhrase !== null)}
        >
          {saving
            ? "Saving..."
            : isNewPhrase
            ? "Create Phrase"
            : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhraseEditDialog;
